import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CornerNotice.css';

export const CORNER_NOTICE_POSITIONS = [
  'bottom-left',
  'bottom-right',
  'top-left',
  'top-right'
];

const POSITION_CLASS = {
  'bottom-left': 'corner-notice--bottom-left',
  'bottom-right': 'corner-notice--bottom-right',
  'top-left': 'corner-notice--top-left',
  'top-right': 'corner-notice--top-right'
};

const EXIT_MS = 280;

/**
 * Floating corner toast — no blocking overlay by default (optional light overlay).
 * API shaped similarly to AlertModal for consistency across the app.
 *
 * @param {boolean} isOpen
 * @param {function} onClose - Called after exit animation completes
 * @param {string} text - Main message
 * @param {string} [title]
 * @param {string} [position] - bottom-left | bottom-right | top-left | top-right
 * @param {string} [containerColor] - Background (css color or gradient)
 * @param {string} [textColor] - Text color override
 * @param {boolean} [showOverlay] - Rare; default false (notification, not modal)
 * @param {number} [overlayOpacity] - When showOverlay is true
 * @param {string} [overlayColor] - rgb triplet e.g. "15, 23, 42" or css color
 * @param {boolean} [dismissOnClick] - Tap/click to dismiss early
 * @param {boolean} [autoClose] - Auto dismiss after viewTime
 * @param {number} [viewTime] - Ms visible before auto close (alias: autoCloseDelay)
 * @param {number} [autoCloseDelay] - Same as viewTime
 * @param {number} [zIndex]
 */
const CornerNotice = ({
  isOpen = false,
  onClose,
  text = '',
  title = null,
  position = 'bottom-left',
  containerColor = null,
  textColor = null,
  showOverlay = false,
  overlayOpacity = 0.12,
  overlayColor = '15, 23, 42',
  dismissOnClick = true,
  autoClose = true,
  viewTime = 5000,
  autoCloseDelay = null,
  zIndex = 10050,
  clickHint = 'Click to dismiss',
  style = null
}) => {
  const [phase, setPhase] = useState('closed');
  const exitTimerRef = useRef(null);
  const autoTimerRef = useRef(null);

  const effectiveViewTime = autoCloseDelay ?? viewTime;
  const positionClass = POSITION_CLASS[position] || POSITION_CLASS['bottom-left'];

  const clearTimers = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const beginExit = useCallback(() => {
    if (phase === 'exit' || phase === 'closed') return;
    clearTimers();
    setPhase('exit');
    exitTimerRef.current = setTimeout(() => {
      setPhase('closed');
      onClose?.();
    }, EXIT_MS);
  }, [clearTimers, onClose, phase]);

  const beginExitRef = useRef(beginExit);
  beginExitRef.current = beginExit;

  useEffect(() => {
    if (!isOpen) return undefined;
    clearTimers();
    setPhase('enter');
    if (autoClose && effectiveViewTime > 0) {
      autoTimerRef.current = setTimeout(() => beginExitRef.current(), effectiveViewTime);
    }
    return clearTimers;
  }, [isOpen, autoClose, effectiveViewTime, clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (phase === 'closed') return null;

  const panelStyle = {
    zIndex,
    ...(containerColor ? { background: containerColor } : {}),
    ...(textColor ? { color: textColor } : {}),
    ...(style || {})
  };

  const overlayStyle = showOverlay
    ? {
        zIndex: zIndex - 10,
        background: overlayColor?.includes(',')
          ? `rgba(${overlayColor}, ${overlayOpacity})`
          : overlayColor || `rgba(15, 23, 42, ${overlayOpacity})`
      }
    : undefined;

  const handleClick = () => {
    if (dismissOnClick) beginExit();
  };

  return createPortal(
    <>
      {showOverlay ? <div className="corner-notice-overlay" style={overlayStyle} aria-hidden /> : null}
      <div
        role="status"
        aria-live="polite"
        className={[
          'corner-notice',
          positionClass,
          phase === 'enter' ? 'corner-notice--enter' : '',
          phase === 'exit' ? 'corner-notice--exit' : '',
          dismissOnClick ? 'corner-notice--clickable' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        style={panelStyle}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (dismissOnClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            beginExit();
          }
        }}
        tabIndex={dismissOnClick ? 0 : undefined}
      >
        {title ? <div className="corner-notice__title">{title}</div> : null}
        <div className="corner-notice__text">{text}</div>
        {dismissOnClick && clickHint ? (
          <div className="corner-notice__hint">{clickHint}</div>
        ) : null}
      </div>
    </>,
    document.body
  );
};

export default CornerNotice;
